import { useSyncExternalStore, useCallback } from 'react';
import {
  clearMaintenanceRefusal,
  getLastMaintenanceRefusal,
  subscribeToMaintenanceRefusal,
  type MaintenanceRefusal,
} from './maintenance';

export interface UseMaintenanceRefusalResult {
  /** The most recent refusal recognised from a typed maintenance 503. */
  refusal: MaintenanceRefusal | null;
  /** Discards the refusal, e.g. once the user has acknowledged the explanation. */
  clear: () => void;
}

/**
 * Exposes the most recent maintenance refusal so a surface can explain that a
 * specific action was refused by a window rather than failing generically.
 *
 * Write controls stay enabled during a window by deliberate decision (see the
 * 11 Aug 2026 decision on NS-2461), so this is the response-side half of the
 * feature: the banner tells the user a window is open, and this tells them
 * their last write did not land.
 *
 * Backed by an external store rather than component state because refusals are
 * recorded from an Axios interceptor and from `maintenanceAwareFetch`, both of
 * which sit outside React.
 */
export function useMaintenanceRefusal(): UseMaintenanceRefusalResult {
  const refusal = useSyncExternalStore(subscribeToMaintenanceRefusal, getLastMaintenanceRefusal);
  const clear = useCallback(() => clearMaintenanceRefusal(), []);

  return { refusal, clear };
}
